<template>
  <CstuModal
    id="adjust-limits-modal"
    z-index="1040"
    size="xl"
    centered
    hide-footer
    :title="'Manage add-ons'"
    @update:model-value="updateVisibility"
  >
    <div>
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-8">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else class="max-h-[80vh] overflow-hidden">
        <div class="grid grid-cols-2 gap-6">
          <!-- Left Column: Add-ons Section -->
          <div class="col-span-1 max-h-[calc(80vh-80px)] overflow-y-auto overflow-x-hidden pr-4">
            <!-- Add-ons Section -->
            <div class="add-ons-container space-y-4 my-2">
              <!-- Limit Items -->
              <LimitItem
                v-for="(item, index) in filteredLimitItems"
                :key="index"
                :title="item.title"
                :limit-key="item.key"
                :value-key="item.valueKey"
                :current-value="currentValue[item.valueKey]"
                :min-value="planLimits[item.valueKey]"
                :used-value="usedLimits[item.valueKey]"
                :price="calculateItemPrice(item)"
                :price-label="item.priceLabel"
                :format-value="item.formatValue"
                :step="item.step || 1"
                :is-annually="isAnnually"
                :addon-count="calculateAddonCount(item.valueKey)"
                :is-agency-plan="isAgencyPlan"
                :max-value="maxValue[item.valueKey]"
                :show-add-action="activeActions[item.valueKey] === 'add'"
                :show-remove-action="activeActions[item.valueKey] === 'remove'"
                @increase="increaseValue(item.valueKey, item.step || 1)"
                @decrease="decreaseValue(item.valueKey, item.step || 1)"
                @input="handleDirectInput(item.valueKey, $event)"
                @add-action="handleAddAction(item.valueKey)"
                @remove-action="handleRemoveAction(item.valueKey)"
                @cancel-action="(originalValue) => handleCancelAction(item.valueKey, originalValue)"
                @close="closeModal"
              />
            </div>
          </div>

          <!-- Right Column: Billing Summary (1/2 width) sticky -->
          <div class="col-span-1 max-h-[calc(80vh-80px)]">
            <div class="sticky bg-gray-100/50 p-6 rounded-lg max-h-[calc(80vh-80px)] overflow-y-auto">
              <!-- Summary Header -->
              <div class="border !border-[#eef1f4] !border-t-0 !border-r-0 !border-l-0 pb-4">
                <div class="flex items-center justify-between">
                  <h4 class="text-xl font-semibold text-gray-900">Billing Summary</h4>
                  <div  class="rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700">
                    {{ isAnnually ? 'Annual Plan' : 'Monthly Plan' }}
                    <i v-tooltip="{ content: billingCycleTooltip, placement: 'top', allowHTML: true }" class="far fa-info-circle ml-1"></i>
                  </div>
                </div>
              </div>

              <!-- Totals -->
              <div class="space-y-4 pt-2 ">
                <!-- Add-on Additions -->
                <div v-if="calculateAdditions > 0" class="flex justify-between items-center">
                  <span class="text-gray-900">Add-on Additions:</span>
                  <span class="text-green-600 font-medium">+${{ calculateAdditions.toFixed(2) }}</span>
                </div>

                <!-- Add-on Removals -->
                <div v-if="calculateRemovals > 0" class="flex justify-between items-center">
                  <span class="text-gray-900">Add-on Removals:</span>
                  <span class="text-red-600 font-medium">-${{ calculateRemovals.toFixed(2) }}</span>
                </div>

                <!-- Annual Discount -->
                <div v-if="hasDiscount" class="flex justify-between items-center">
                  <span class="text-gray-900">Discount:</span>
                  <span class="text-blue-600 font-medium">{{ discountPercentage }}%</span>
                </div>

                <!-- Net Adjustment -->
                <div class="flex justify-between items-center pt-4">
                  <span class="text-gray-900 font-medium">Net Adjustment:</span>
                  <span :class="{ 'text-green-600': calculateNetAdjustment > 0,'text-red-600': calculateNetAdjustment < 0, 'text-gray-800': calculateNetAdjustment === 0 }" class="font-medium">
                    {{ calculateNetAdjustment > 0 ? '+' : '' }}${{ calculateNetAdjustment.toFixed(2) }}
                  </span>
                </div>

                <!-- Current Plan Cost -->
                <!-- <div class="flex justify-between items-center">
                  <span class="text-gray-900">Current Plan Cost:</span>
                  <span class="text-gray-900">${{ totalAdditionalCost }}</span>
                </div> -->

                <!-- New Total -->
                <div class="flex justify-between items-center border-t !border-[#eef1f4] pt-4 font-bold text-lg">
                  <span>New Total:</span>
                  <span :class="`${calculateNetAdjustment < 0 ? 'text-red-600' : 'text-gray-900'}`">
                    ${{ totalAdditionalCost }}
                  </span>
                </div>
              </div>

              <!-- Billing Details -->
              <div class="mt-6 space-y-4 text-sm text-gray-800 border !border-[#eef1f4] !border-b-0 !border-r-0 !border-l-0 pt-4">
                <p class=" text-sm">Amount calculated does not include VAT & Sales Tax. </p>
                <p v-tooltip="{ content: 'You will be charged or refunded only for the remaining days of the current month' } " class="text-sm w-max">Prorated charges will apply.</p>
                <p class=" text-sm text-green-600"> <i class="far fa-check-circle mr-1"></i>Changes will take effect immediately after confirmation</p>
              </div>

              <!-- Action Buttons -->
              <div class="mt-5 flex justify-between items-center">
                <a
                  href="#"
                  class="text-sm text-blue-500 beacon"
                  data-beacon-article-modal="68149dc39b289a0db02fa8aa"
                >Learn more</a>

               <div class="flex space-x-4">
                <Button variant="text"  color='secondary' :disabled="isLoading" @click="closeModal">Cancel</Button>
                <Button
                  color="primary"
                  :loading="isLoading"
                  :disabled="!hasChanges || isLoading"
                  @click="handlePurchase"
                >
                  Confirm Purchase <i class="far fa-arrow-right ml-1"></i>
                </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </CstuModal>
</template>

<script setup>
import { h, inject, ref, computed } from 'vue'
import { Button } from '@contentstudio/ui'
import { useAdjustLimits } from '@modules/billing/composables/useAdjustLimits'
import LimitItem from './LimitItem.vue'

// Emits
const emit = defineEmits(['update:visibility'])

// Inject dependencies
const root = inject('root')
const { $cstuModal } = root
const store = inject('store')

// Composables
const {
  isAnnually,
  isAgencyPlan,
  hasChanges,
  loading,
  planLimits,
  usedLimits,
  currentValue,
  maxValue,
  isLoading,
  filteredLimitItems,
  totalAdditionalCost,
  increaseValue,
  decreaseValue,
  handleDirectInput,
  calculateItemPrice,
  calculateAddonCount,
  fetchSubscriptionLimits,
  addonAdjustments,
  handlePurchase: purchaseLimits,
  planSlug
} = useAdjustLimits()

// Track which items are currently in add or remove mode
const activeActions = ref({})

const billingCycleTooltip = computed(() => {
  return `<span>Add-ons follow the same billing cycle as your plan.</span> <br>
  <span>You’ll be billed immediately (prorated) for the remaining time in your current cycle.</span> <br>
  <span>Your plan and add-ons will be billed together as a single subscription from your next billing date.</span>`
})

// Calculate the total cost of all additions
const calculateAdditions = computed(() => {
  let additions = 0

  // Sum up all items with operation 'add' and calculate the total cost for annual or monthly
  Object.values(addonAdjustments.value).forEach(detail => {
    if (detail && detail.operation === 'add') {
      additions += detail.itemCount * detail.pricePerItem * (isAnnually.value ? 12 : 1)
    }
  })

  return additions
})

// Calculate the total cost of all removals
const calculateRemovals = computed(() => {
  let removals = 0

  // Sum up all items with operation 'remove' and calculate the total cost for annual or monthly
  Object.values(addonAdjustments.value).forEach(detail => {
    if (detail && detail.operation === 'remove') {
      removals += detail.itemCount * detail.pricePerItem * (isAnnually.value ? 12 : 1)
    }
  })

  return removals
})

// Calculate the net adjustment (additions minus removals)
const calculateNetAdjustment = computed(() => {
  const netAdjustment = calculateAdditions.value - calculateRemovals.value
  return netAdjustment
})

// Calculate discount percentage based on plan slug
const discountPercentage = computed(() => {
  if (isAnnually.value) {
    switch (planSlug.value) {
      case 'standard-annually':
        return 34.48 // 34.48% discount
      case 'advanced-annually':
        return 28.99 // 28.99% discount
      case 'agency-unlimited-annually':
        return 28.78 // 28.78% discount
      default:
        return 0
    }
  }
  return 0
})

// Check if there's a discount to show
const hasDiscount = computed(() => {
  return isAnnually.value && discountPercentage.value > 0
})

const closeModal = () => {
  $cstuModal.hide('adjust-limits-modal')
}

const updateVisibility = (value) => {
  if (value) {
    // Modal is opening, fetch subscription limits
    fetchSubscriptionLimits(store.getters.getActiveWorkspace?._id)
  }
  // Reset active actions when modal visibility changes
  activeActions.value = {}
  addonAdjustments.value = {}
  emit('update:visibility', value)
}

// Handle add action button click
const handleAddAction = (valueKey) => {
  // Toggle off if already active
  if (activeActions.value[valueKey] === 'add') {
    const newActiveActions = { ...activeActions.value }
    delete newActiveActions[valueKey]
    activeActions.value = newActiveActions
  } else {
    // Set to add mode for this item (keep other items open)
    activeActions.value = {
      ...activeActions.value,
      [valueKey]: 'add'
    }
  }
}

// Handle remove action button click
const handleRemoveAction = (valueKey) => {
  // Toggle off if already active
  if (activeActions.value[valueKey] === 'remove') {
    const newActiveActions = { ...activeActions.value }
    delete newActiveActions[valueKey]
    activeActions.value = newActiveActions
  } else {
    // Set to remove mode for this item (keep other items open)
    activeActions.value = {
      ...activeActions.value,
      [valueKey]: 'remove'
    }
  }
}

// Handle cancel action button click
const handleCancelAction = (valueKey, originalValue) => {
  console.log('handleCancelAction', valueKey, originalValue)
  // Reset active action for this item only
  if (valueKey) {
    const newActiveActions = { ...activeActions.value }
    delete newActiveActions[valueKey]
    activeActions.value = newActiveActions

    // Reset the current value to the original value
    currentValue.value[valueKey] = originalValue
  } else {
    // Reset all active actions if no valueKey provided
    activeActions.value = {}
  }
}

const handlePurchase = async () => {
  // Using HTML string
  const messageVNode = h('div', { class: ['d-flex', 'flex-column'] }, [
    h('div', {
      innerHTML:
        'Are you sure you want to proceed with this purchase? <br /> Payment will be automatically processed.',
    }),
  ])

  const res = await $cstuModal.msgBoxConfirm(
    messageVNode,
    {
      title: 'Confirm Purchase',
      zIndex: 1080,
      okTitle: 'Confirm',
    }
  )

  if (!res) {
    return
  }

  const success = await purchaseLimits(store.getters.getActiveWorkspace?._id)

  if (success) {
    setTimeout(() => {
      // reload the page to update the limits
      window.location.reload();
    }, 2000) // 2 seconds delay
  }
}
</script>
